<template>
  <div class="kyc-dashboard-page">
    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ $t('admin.compliance.kyc_dashboard.title') }}</h1>
        <p class="page-subtitle">{{ $t('admin.compliance.kyc_dashboard.subtitle') }}</p>
      </div>
      <div class="header-actions">
        <input v-model="startDate" type="date" class="date-input" @change="fetchDashboard" />
        <span class="date-sep">{{ $t('admin.compliance.kyc_dashboard.date_to') }}</span>
        <input v-model="endDate" type="date" class="date-input" @change="fetchDashboard" />
        <button class="btn btn-secondary" @click="fetchDashboard">{{ $t('admin.compliance.kyc_dashboard.btn_refresh') }}</button>
      </div>
    </div>

    <!-- Stat Cards -->
    <div v-if="loading" class="stat-grid">
      <div v-for="n in 6" :key="n" class="stat-card stat-card--skeleton">
        <div class="skeleton-value"></div>
        <div class="skeleton-label"></div>
      </div>
    </div>

    <div v-else class="stat-grid">
      <div class="stat-card stat-card--orange">
        <div class="stat-value">{{ stats.pending_count.toLocaleString() }}</div>
        <div class="stat-label">{{ $t('admin.compliance.kyc_dashboard.stat_pending') }}</div>
        <div class="stat-hint">{{ $t('admin.compliance.kyc_dashboard.current_queue') }}</div>
      </div>

      <div class="stat-card stat-card--green">
        <div class="stat-value">{{ stats.auto_approved_count.toLocaleString() }}</div>
        <div class="stat-label">{{ $t('admin.compliance.kyc_dashboard.stat_auto_approved') }}</div>
        <div class="stat-hint">{{ dateRangeLabel }}</div>
      </div>

      <div class="stat-card stat-card--red">
        <div class="stat-value">{{ stats.auto_rejected_count.toLocaleString() }}</div>
        <div class="stat-label">{{ $t('admin.compliance.kyc_dashboard.stat_auto_rejected') }}</div>
        <div class="stat-hint">{{ dateRangeLabel }}</div>
      </div>

      <div class="stat-card stat-card--blue">
        <div class="stat-value">{{ stats.manual_review_count.toLocaleString() }}</div>
        <div class="stat-label">{{ $t('admin.compliance.kyc_dashboard.stat_manual_reviews') }}</div>
        <div class="stat-hint">{{ dateRangeLabel }}</div>
      </div>

      <div class="stat-card stat-card--neutral">
        <div class="stat-value">
          {{ stats.avg_processing_time_hours !== null ? stats.avg_processing_time_hours + 'h' : '—' }}
        </div>
        <div class="stat-label">{{ $t('admin.compliance.kyc_dashboard.stat_avg_processing') }}</div>
        <div class="stat-hint">{{ dateRangeLabel }}</div>
      </div>

      <div class="stat-card stat-card--green">
        <div class="stat-value">{{ stats.provider_uptime_percent }}%</div>
        <div class="stat-label">{{ $t('admin.compliance.kyc_dashboard.stat_provider_uptime') }}</div>
        <div class="stat-hint">{{ $t('admin.compliance.kyc_dashboard.last_30_days') }}</div>
      </div>
    </div>

    <!-- Summary Table -->
    <div class="summary-card">
      <div class="summary-header">
        <div class="summary-title">{{ $t('admin.compliance.kyc_dashboard.summary_title') }}</div>
        <div class="summary-period">{{ dateRangeLabel }}</div>
      </div>

      <div v-if="loading" class="summary-loading">
        <span class="spinner"></span>
        {{ $t('admin.compliance.kyc_dashboard.loading') }}
      </div>

      <table v-else class="summary-table">
        <thead>
          <tr>
            <th>{{ $t('admin.compliance.kyc_dashboard.col_metric') }}</th>
            <th class="text-right">{{ $t('admin.compliance.kyc_dashboard.col_count') }}</th>
            <th>{{ $t('admin.compliance.kyc_dashboard.col_details') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <span class="badge badge-orange">Pending</span>
              Pending Verifications
            </td>
            <td class="text-right mono">{{ stats.pending_count.toLocaleString() }}</td>
            <td class="text-muted">Players awaiting review (current snapshot)</td>
          </tr>
          <tr>
            <td>
              <span class="badge badge-green">Auto</span>
              Auto-Approved
            </td>
            <td class="text-right mono">{{ stats.auto_approved_count.toLocaleString() }}</td>
            <td class="text-muted">Verified by provider webhook</td>
          </tr>
          <tr>
            <td>
              <span class="badge badge-red">Auto</span>
              Auto-Rejected
            </td>
            <td class="text-right mono">{{ stats.auto_rejected_count.toLocaleString() }}</td>
            <td class="text-muted">Failed provider verification</td>
          </tr>
          <tr>
            <td>
              <span class="badge badge-blue">Manual</span>
              Manual Reviews
            </td>
            <td class="text-right mono">{{ stats.manual_review_count.toLocaleString() }}</td>
            <td class="text-muted">Reviewed by compliance officers</td>
          </tr>
          <tr>
            <td>
              <span class="badge badge-neutral">Avg</span>
              Avg Processing Time
            </td>
            <td class="text-right mono">
              {{ stats.avg_processing_time_hours !== null ? stats.avg_processing_time_hours + 'h' : '—' }}
            </td>
            <td class="text-muted">From document upload to review decision</td>
          </tr>
          <tr>
            <td>
              <span class="badge badge-green">Live</span>
              Provider Uptime
            </td>
            <td class="text-right mono">{{ stats.provider_uptime_percent }}%</td>
            <td class="text-muted">KYC provider availability (last 30 days)</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'
import { useToastStore } from '@/stores/toast'

const { t } = useI18n()
const toast = useToastStore()

// ----------- Types -----------

interface KycDashboardStats {
  pending_count: number
  auto_approved_count: number
  auto_rejected_count: number
  manual_review_count: number
  avg_processing_time_hours: number | null
  provider_uptime_percent: number
  date_range: {
    start: string
    end: string
  }
}

// ----------- State -----------

const loading = ref(true)
const stats = ref<KycDashboardStats>({
  pending_count: 0,
  auto_approved_count: 0,
  auto_rejected_count: 0,
  manual_review_count: 0,
  avg_processing_time_hours: null,
  provider_uptime_percent: 99.9,
  date_range: { start: '', end: '' },
})

// Default to last 30 days
const defaultEnd = new Date()
const defaultStart = new Date()
defaultStart.setDate(defaultStart.getDate() - 30)

const startDate = ref(defaultStart.toISOString().split('T')[0])
const endDate = ref(defaultEnd.toISOString().split('T')[0])

// ----------- Computed -----------

const dateRangeLabel = computed(() => {
  if (!stats.value.date_range.start) return ''
  return `${stats.value.date_range.start} — ${stats.value.date_range.end}`
})

// ----------- Data fetching -----------

async function fetchDashboard(): Promise<void> {
  loading.value = true
  try {
    const response = await apiClient.get('/admin/api/kyc/dashboard', {
      params: {
        start_date: startDate.value,
        end_date: endDate.value,
      },
    })
    stats.value = response.data as KycDashboardStats
  } catch {
    toast.error(t('admin.compliance.kyc_dashboard.error_load'))
  } finally {
    loading.value = false
  }
}

// ----------- Lifecycle -----------

onMounted(async () => {
  await fetchDashboard()
})
</script>

<style scoped>
.kyc-dashboard-page {
  padding: 24px;
  animation: fadeIn 0.3s ease both;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Page Header */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 12px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 4px;
}

.page-subtitle {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.date-input {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 8px 12px;
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
}

.date-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.date-sep {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: var(--radius-md, 8px);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: all 150ms ease;
}

.btn-secondary {
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-secondary:hover {
  background: #2a4455;
  color: var(--text-primary, #fff);
}

/* Stat Grid */
.stat-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  padding: 20px;
  border-left: 4px solid transparent;
  transition: all 150ms ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}

.stat-card--orange {
  border-left-color: #ffa726;
}

.stat-card--green {
  border-left-color: #00e701;
}

.stat-card--red {
  border-left-color: #ef5350;
}

.stat-card--blue {
  border-left-color: #1475e1;
}

.stat-card--neutral {
  border-left-color: #7f8fa4;
}

.stat-value {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 32px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin-bottom: 4px;
  line-height: 1;
}

.stat-label {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
}

.stat-hint {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  font-style: italic;
}

/* Skeleton */
.stat-card--skeleton {
  border-left-color: rgba(255, 255, 255, 0.05);
}

.skeleton-value {
  height: 32px;
  width: 60%;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  margin-bottom: 8px;
  animation: pulse 1.5s ease-in-out infinite;
}

.skeleton-label {
  height: 12px;
  width: 40%;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  animation: pulse 1.5s ease-in-out infinite 0.2s;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
}

/* Summary Card */
.summary-card {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  padding: 20px;
}

.summary-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.summary-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary, #fff);
}

.summary-period {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
}

.summary-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 32px;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 14px;
}

.summary-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.summary-table th {
  text-align: left;
  padding: 10px 12px;
  font-size: 11px;
  font-weight: 600;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.summary-table th.text-right {
  text-align: right;
}

.summary-table td {
  padding: 12px 12px;
  color: var(--text-secondary, #b1bad3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  vertical-align: middle;
}

.summary-table tr:last-child td {
  border-bottom: none;
}

.text-right {
  text-align: right;
}

.text-muted {
  color: var(--text-tertiary, #7f8fa4) !important;
  font-size: 12px;
}

.mono {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-weight: 600;
  color: var(--text-primary, #fff);
}

/* Badges */
.badge {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-right: 6px;
  vertical-align: middle;
}

.badge-orange {
  background: rgba(255, 167, 38, 0.15);
  color: #ffa726;
}

.badge-green {
  background: rgba(0, 231, 1, 0.15);
  color: #00e701;
}

.badge-red {
  background: rgba(239, 83, 80, 0.15);
  color: #ef5350;
}

.badge-blue {
  background: rgba(20, 117, 225, 0.15);
  color: #1475e1;
}

.badge-neutral {
  background: rgba(127, 143, 164, 0.15);
  color: #7f8fa4;
}

/* Spinner */
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive */
@media (max-width: 1200px) {
  .stat-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .kyc-dashboard-page {
    padding: 16px;
  }

  .stat-grid {
    grid-template-columns: 1fr 1fr;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-actions {
    flex-wrap: wrap;
  }
}
</style>
